import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Box from '@app/components/common/Box';
import Button from '@app/components/common/Button';
import {
  selectChangeMassUpdateDataSelectionCriteria,
  selectFinalDiffObject,
  selectMenuTypeVersionsOfCurrentItem,
  selectNumberOfMeuTypeVersions,
} from '@app/state/menu/menuSelectors';
import { useDispatch, useSelector } from 'react-redux';
import SelectMenuTypeToApplyChangesModal from '../SpecialsDiffModal/SelectMenuTypeToApplyChangesModal';
import {
  actionCreatorsMenu,
  IItemDiffObject,
  IMenuTypeToSelect,
} from '@westondev/tableturn-core';
import useRootSelector from '@app/hooks/useRootSelector';
import { WithTranslation } from 'react-i18next';
import { actionCreatorsMenuWeb } from '@app/state';
import { bindActionCreators } from '@reduxjs/toolkit';
import { menuTypeVersionsSelectionFactory } from '@app/helpers/factories/menu/cardFactories/menuTypes/menuTypeVersionsFactory';

interface ApplyToMenusButtonsProps extends WithTranslation {
  selectedMenuTypeVersion: number;
  setSelectedMenuTypeVersion: Dispatch<SetStateAction<number>>;
}

const ApplyToMenusButtons = ({
  t,
  selectedMenuTypeVersion,
  setSelectedMenuTypeVersion,
}: ApplyToMenusButtonsProps) => {
  const dispatch = useDispatch();

  const [applyChangesModalActive, setApplyChangesModalActive] = useState(false);
  const [appliedChanges, setAppliedChanges] = useState<{
    currentMenuType: number;
    menuTypesToApplyChanges: IMenuTypeToSelect[];
  } | null>(null);

  const { setDiffObject } = bindActionCreators(actionCreatorsMenu, dispatch);
  const { applyChangesToMenuTypesWeb } = bindActionCreators(
    actionCreatorsMenuWeb,
    dispatch,
  );

  const massUpdateSelectionCriteria = useSelector(
    selectChangeMassUpdateDataSelectionCriteria,
  );
  const finalDiffObject = useSelector(selectFinalDiffObject);
  const menuTypeVersions = useSelector(selectMenuTypeVersionsOfCurrentItem);

  const numberOfMeuTypeVersions = useRootSelector(state =>
    !!massUpdateSelectionCriteria === false
      ? selectNumberOfMeuTypeVersions(state)
      : 0,
  );

  const handleApplyChanges = (menuTypes: IMenuTypeToSelect[]) => {
    setAppliedChanges({
      currentMenuType: selectedMenuTypeVersion,
      menuTypesToApplyChanges: menuTypes,
    });
  };

  const handleApplyChangesToAllMenus = () => {
    const menuTypeVersionId =
      formattedDifferences?.menuTypeVersions?.[selectedMenuTypeVersion]?.id;
    const formattedMenuTypes = menuTypeVersions
      ? menuTypeVersionsSelectionFactory(menuTypeVersions).filter(
          menuTypeVersion => menuTypeVersion.menuTypeId !== menuTypeVersionId,
        )
      : [];
    setAppliedChanges({
      currentMenuType: selectedMenuTypeVersion,
      menuTypesToApplyChanges: formattedMenuTypes,
    });
  };

  const formattedDifferences = finalDiffObject as IItemDiffObject;

  useEffect(() => {
    if (formattedDifferences && appliedChanges) {
      applyChangesToMenuTypesWeb(appliedChanges, ({ newMenuTypeVersions }) => {
        const newFinalDiffObject = {
          ...formattedDifferences,
          menuTypeVersions: newMenuTypeVersions,
        };
        setDiffObject(newFinalDiffObject);
        setAppliedChanges(null);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedChanges]);

  useEffect(() => {
    const selectedMenuTypeVersionId =
      formattedDifferences.menuTypeVersions[selectedMenuTypeVersion]?.id;
    const newMenuTypeVersionIndex =
      formattedDifferences.menuTypeVersions.findIndex(
        menuTypeVersion => menuTypeVersion.id === selectedMenuTypeVersionId,
      );

    if (
      newMenuTypeVersionIndex !== -1 &&
      newMenuTypeVersionIndex !== selectedMenuTypeVersion
    ) {
      setSelectedMenuTypeVersion(newMenuTypeVersionIndex);
    }
  }, [
    formattedDifferences.menuTypeVersions,
    setSelectedMenuTypeVersion,
    selectedMenuTypeVersion,
  ]);

  return (
    <>
      <SelectMenuTypeToApplyChangesModal
        isActive={applyChangesModalActive}
        setModalActive={() => setApplyChangesModalActive(false)}
        onApplyChanges={handleApplyChanges}
        menuTypeId={
          formattedDifferences &&
          formattedDifferences.menuTypeVersions.length > 0
            ? formattedDifferences.menuTypeVersions[selectedMenuTypeVersion].id
            : 0
        }
      />
      {!!massUpdateSelectionCriteria === false && (
        <Box csx={{ display: 'flex' }}>
          <Button
            variant="primary"
            onClick={() => setApplyChangesModalActive(true)}
            csx={{ marginRight: '10px', padding: '0 20px' }}
            disabled={numberOfMeuTypeVersions === 1}>
            {t(
              'app.modals.itemsDiff.menuType.applyChangesToSpecificMenusTypes.title',
            )}
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyChangesToAllMenus}
            csx={{ padding: '0 20px' }}
            disabled={numberOfMeuTypeVersions === 1}>
            {t(
              'app.modals.itemsDiff.menuType.applyChangesToAllMenusTypes.title',
            )}
          </Button>
        </Box>
      )}
    </>
  );
};

export default ApplyToMenusButtons;
